import type { BreakdownSelectionPayload } from "@/features/app-studio/api/appBreakdownsApi";
import type {
  AppTableDataItem,
  AppTableSortConfig,
} from "@/features/app-studio/api/appTablesApi";
import { parsePublishedAppItemsSseChunk } from "@/features/published-apps/lib/publishedAppItemsStream";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

/** What one table-block request names: the widget, where to continue from, and in what order. */
export type TableBlockRequest = {
  tableId: number;
  /** The opaque `next_cursor` of the block before, or null for the first block. */
  cursor: string | null;
  /**
   * The viewer's own column sort. Absent or null orders by the widget's saved sort; `[]` asks for
   * no sort at all. A cursor is only valid with the sort the block it came from was fetched with.
   */
  sort?: readonly AppTableSortConfig[] | null;
  /**
   * The breakdown option the table's rows on screen were run for. A cursor only means something
   * against the result it came from, so the next block must be cut the same way.
   */
  breakdowns?: BreakdownSelectionPayload;
};

/**
 * The query string of a table-block request, shared by every surface's page-turn call so they
 * cannot disagree about how a sort is spelled. JSON, because a column name may hold any character.
 */
export function tableBlockSearchParams(params: TableBlockRequest): URLSearchParams {
  const query = new URLSearchParams({ table_id: String(params.tableId) });
  if (params.cursor !== null) {
    query.set("cursor", params.cursor);
  }
  if (params.sort !== undefined && params.sort !== null) {
    query.set("sort", JSON.stringify(params.sort));
  }
  // Repeated `breakdown=<id>:<option_key>`: an option key is `[a-z0-9_]`, so the colon is safe.
  for (const [breakdownId, optionKey] of Object.entries(params.breakdowns ?? {})) {
    query.append("breakdown", `${breakdownId}:${optionKey}`);
  }
  return query;
}

/**
 * Read a page-scoped items stream down to the single table item it carries.
 *
 * A table page turn re-requests the same SSE endpoint scoped to one widget (`table_id`, `page`,
 * `cursor`), so the response is a short stream — one `item` frame for that table plus a terminal
 * `done` frame — rather than the whole app's payload. HTTP SSE cannot push into an
 * already-drained response, which is why a page turn is a new request rather than a message on
 * the original stream.
 *
 * Reuses `parsePublishedAppItemsSseChunk` unmodified: the frames are the same shape the full-app
 * stream emits, just far fewer of them. Shared by the builder, published, and share-link page-turn
 * calls, which differ only in path and fetcher.
 */
export async function readTablePageResponse(response: Response): Promise<AppTableDataItem> {
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response));
  }
  if (!response.body) {
    throw new Error("Page response did not include a body.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let table: AppTableDataItem | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const parsed = parsePublishedAppItemsSseChunk(buffer);
    buffer = parsed.remainder;
    for (const event of parsed.events) {
      if (event.type === "item" && event.item.item_type === "table") {
        table = event.item;
      }
    }
  }

  if (table === null) {
    throw new Error("Page response returned no table.");
  }
  return table;
}
